export interface DashboardReference {
    id: string;
    dashboardId: string;
    dashboardTitle: string;
    kind: 'element' | 'source' | 'dashboard';
    label: string;
    context: string;
}

const EDIT_INSTRUCTION =
    'Change only what the user asks about this reference with edit_dashboard, passing this dashboard_id and copying old_str exactly from the HTML above. Call get_dashboard only if you need context that is not shown here.';

const MAX_ELEMENT_HTML_CHARS = 4000;

const truncated = (html: string): string =>
    html.length > MAX_ELEMENT_HTML_CHARS
        ? `${html.slice(
              0,
              MAX_ELEMENT_HTML_CHARS
          )}… (cut off, use get_dashboard for the rest)`
        : html;

export const upsertReference = (
    references: DashboardReference[],
    reference: DashboardReference
): DashboardReference[] => [
    ...references.filter((existing) => existing.id !== reference.id),
    reference
];

export type ReferenceChip = Pick<
    DashboardReference,
    'dashboardTitle' | 'kind' | 'label'
>;

const REFERENCES_BLOCK =
    /^<dashboard_references>[\s\S]*?<\/dashboard_references>\n\n/;
const REFERENCE_HEADER = /<reference>(\{.*\})\n/g;

export const withReferences = (
    message: string,
    references: DashboardReference[]
): string => {
    if (references.length === 0) return message;
    const blocks = references
        .map((reference) => {
            const chip: ReferenceChip = {
                dashboardTitle: reference.dashboardTitle,
                kind: reference.kind,
                label: reference.label
            };
            return `<reference>${JSON.stringify(chip)}\n${
                reference.context
            }\n</reference>`;
        })
        .join('\n');
    return `<dashboard_references>\n${blocks}\n\n${EDIT_INSTRUCTION}\n</dashboard_references>\n\n${message}`;
};

export const splitReferences = (
    output: string
): { block: string; chips: ReferenceChip[]; text: string } => {
    const block = output.match(REFERENCES_BLOCK)?.[0] ?? '';
    const chips = [...block.matchAll(REFERENCE_HEADER)].flatMap((match) => {
        try {
            return [JSON.parse(match[1]) as ReferenceChip];
        } catch {
            return [];
        }
    });
    return { block, chips, text: output.slice(block.length) };
};

const REFERENCED_DASHBOARD = /^Dashboard "(.*)" \(dashboard_id ([^)\s]+)\)/gm;

export const referencedDashboardsOf = (
    output: string
): { id: string; title: string }[] => {
    const found = new Map<string, string>();
    for (const match of splitReferences(output).block.matchAll(
        REFERENCED_DASHBOARD
    )) {
        if (!found.has(match[2])) found.set(match[2], match[1]);
    }
    return [...found].map(([id, title]) => ({ id, title }));
};

const sourceLines = (
    sourceId: string,
    datasets: { id: string; title: string }[]
): string[] => {
    if (!sourceId) return [];
    const title = datasets.find((dataset) => dataset.id === sourceId)?.title;
    return [
        `Reads data source: ${
            title ? `"${title}" ` : ''
        }(dataset id ${sourceId})`
    ];
};

export const elementReference = (
    dashboard: { id: string; title: string },
    pick: { label: string; selector: string; html: string; source: string },
    datasets: { id: string; title: string }[] = []
): DashboardReference => ({
    id: `element:${dashboard.id}:${pick.selector}`,
    dashboardId: dashboard.id,
    dashboardTitle: dashboard.title,
    kind: 'element',
    label: pick.label,
    context: [
        `Dashboard "${dashboard.title}" (dashboard_id ${dashboard.id}), page element: ${pick.label}`,
        `CSS path inside the page: ${pick.selector}`,
        `HTML: ${truncated(pick.html)}`
    ].join('\n')
});

export const sourceReference = (
    dashboard: { id: string; title: string },
    dataset: {
        id: string;
        title: string;
        connector: string;
        tool: string;
        args?: Record<string, unknown>;
    }
): DashboardReference => ({
    id: `source:${dashboard.id}:${dataset.id}`,
    dashboardId: dashboard.id,
    dashboardTitle: dashboard.title,
    kind: 'source',
    label: `Source - "${dataset.title}"`,
    context: [
        `Dashboard "${dashboard.title}" (dashboard_id ${dashboard.id}), data source "${dataset.title}" (dataset id ${dataset.id})`,
        `Connector: ${dataset.connector}, tool: ${dataset.tool}`,
        `Args: ${JSON.stringify(dataset.args ?? {})}`
    ].join('\n')
});

export const dashboardReference = (dashboard: {
    id: string;
    title: string;
    html: string;
    datasets: {
        id: string;
        title: string;
        connector: string;
        tool: string;
        args?: Record<string, unknown>;
    }[];
}): DashboardReference => ({
    id: `dashboard:${dashboard.id}`,
    dashboardId: dashboard.id,
    dashboardTitle: dashboard.title,
    kind: 'dashboard',
    label: 'Whole dashboard',
    context: [
        `Dashboard "${dashboard.title}" (dashboard_id ${dashboard.id}), the whole dashboard`,
        'Data sources:',
        ...dashboard.datasets.map(
            (dataset) =>
                `- "${dataset.title}" (dataset id ${dataset.id}), connector ${
                    dataset.connector
                }, tool ${dataset.tool}, args ${JSON.stringify(
                    dataset.args ?? {}
                )}`
        ),
        'The page HTML is not included: call get_dashboard to read it.'
    ].join('\n')
});
